import React, { useCallback } from 'react';
import { css } from '@compiled/react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CommentIcon from '@atlaskit/icon/core/comment';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';

import {
	usePanel,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useContentType } from '@confluence/page-context';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { useInlineCommentsContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { UnreadIndicator } from '@confluence/unread-comments/entry-points/UnreadIndicator.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER,
	UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';

const commentIconContainerStyle = css({
	position: 'relative',
});

const i18n = defineMessages({
	openCommentsPanelLabel: {
		id: 'object-sidebar-components.open.comments.panel.label',
		defaultMessage: 'Open Comments Panel',
		description: 'Label for button to open the comments panel',
	},
	viewAllCommentsLabel: {
		id: 'object-sidebar-components.view.all.comments',
		defaultMessage: 'View all comments',
		description: 'Label for button to view all comments by opening the comments panel',
	},
	showCommentsLabel: {
		id: 'object-sidebar-components.show.comments.label',
		description:
			"The text is shown in a tooltip when the Object Sidebar's Comments button is hovered.",
		defaultMessage: 'Show comments',
	},
});

export type CommentPanelButtonProps = {
	contentId: string;
	collapsed?: boolean;
	testId?: string;
	isStandaloneComment?: boolean;
	isDisabled?: boolean;
	isViewAllIcon?: boolean;
};

export const CommentPanelButton = ({
	collapsed,
	testId,
	isStandaloneComment = false,
	isDisabled = false,
	isViewAllIcon = false,
}: CommentPanelButtonProps) => {
	const pageMode = useGetPageMode();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();
	const {
		viewProps,
		selectionProps,
		hoverProps,
		editorViewProps,
		editorCreateProps,
		showCreateComponent,
		selectionOptions,
	} = useAnnotations();
	const {
		setViewProps,
		setSelectionProps,
		setHoverProps,
		setEditorViewProps,
		setEditorCreateProps,
		setShowCreateComponent,
		setSelectionOptions,
	} = useAnnotationsDispatch();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const panel = usePanel();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const eventEmitter =
		pageMode === PageMode.VIEW
			? getRendererAnnotationEventEmitter()
			: getEditorAnnotationEventEmitter();
	const { newCommentHighlight, activeHighlight } = useInlineCommentsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const { formatMessage } = useIntl();
	const [{ isViewMode: isLiveViewMode, isEditMode: isLiveEditMode }] = useLivePageMode();
	const dataTestId = testId || 'object-sidebar-comments-button-id';
	const [contentTypeFromState] = useContentType();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { closeCommentContainer } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();
	const allowComments = useEmbeddedComments();

	const { unreadCommentIDSet } = useUnreadCommentsState();

	const commentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
		isEditorShortcut: pageMode === 'edit' || isLiveEditMode,
	});

	// keyboard events do not bubble up to confluence when focus is in the db iframe.
	// Since the keyboard shortcut does not work, we must hide the shortcut text.
	const isShortcutTextShown = contentTypeFromState !== ContentType.DATABASE;

	const handleCommentClick = useCallback(() => {
		// FABRIC PAGES
		// Close the view component if it's open
		if (viewProps) {
			// Inform the Renderer to de-select the annotation
			eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
			eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
			// Remove the display of the comment
			setViewProps(null);
		}
		if (showCreateComponent && selectionOptions) {
			// Inform CreateComment to reset the state
			setShowCreateComponent(false);
			setSelectionOptions(undefined);
		}

		if (editorViewProps) {
			// Inform the editor to de-select the annotation
			eventEmitter.emit('setselectedannotation', '');
			// Remove the display of the comment
			setEditorViewProps(null);
		}

		if (selectionProps) {
			selectionProps.onClose();
			setSelectionProps(null);
		}

		if (hoverProps) {
			hoverProps.onClose();
			setHoverProps(null);
		}

		if (editorCreateProps) {
			editorCreateProps?.onClose && editorCreateProps.onClose();
			setEditorCreateProps(null);
		}

		// LEGACY PAGES
		if (newCommentHighlight || activeHighlight) {
			// Close the legacy inline comment panel
			window.dispatchEvent(new Event('closeLegacyInlineCommentSidebar'));
		}

		// Should unmount the expanded comment editor
		// standalone reply, object footer comment
		if (expanded && !fg('confluence_comment_panel_close_footer')) {
			closeCommentContainer();
		}
		if (panel?.id === 'commentsPanel') {
			void hideObjectSidebar();
		} else {
			void showCommentsPanel();
		}
	}, [
		eventEmitter,
		viewProps,
		editorViewProps,
		selectionProps,
		hoverProps,
		newCommentHighlight,
		activeHighlight,
		setViewProps,
		setEditorViewProps,
		setSelectionProps,
		setHoverProps,
		showCommentsPanel,
		hideObjectSidebar,
		panel,
		editorCreateProps,
		closeCommentContainer,
		expanded,
		setEditorCreateProps,
		showCreateComponent,
		selectionOptions,
		setShowCreateComponent,
		setSelectionOptions,
	]);

	const checkUnsavedOnClickCommentsButton = useCallback(
		({
			triggeredByKeyboardShortcut = false,
			isViewAllIcon = false,
		}: {
			triggeredByKeyboardShortcut?: boolean;
			isViewAllIcon?: boolean;
		} = {}) =>
			() => {
				// Early return for ViewAll icon - no action needed
				if (isViewAllIcon) {
					return;
				}

				if (hasContentChanged) {
					showModal(CommentWarningDialog, {
						onConfirm: () => {
							resetContentChanged();
							handleCommentClick();
							if (fg('confluence_comments_reset_data_on_comment_discard')) {
								resetInitialDataLoadedMap();
							}
						},
					});
				} else {
					handleCommentClick();
				}

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'commentsPanel',
						source: 'contentWrapper',
						attributes: {
							triggeredByKeyboardShortcut,
							contentType: contentTypeFromState,
							pageMode,
						},
					},
				}).fire();
			},
		[
			hasContentChanged,
			handleCommentClick,
			resetContentChanged,
			resetInitialDataLoadedMap,
			showModal,
			createAnalyticsEvent,
			contentTypeFromState,
			pageMode,
		],
	);

	const shortcutListeners = (
		<>
			{(pageMode === 'view' || isLiveViewMode) && (
				<GeneralShortcutListener
					deprecatedAccelerator={TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER}
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={checkUnsavedOnClickCommentsButton({ triggeredByKeyboardShortcut: true })}
				/>
			)}
			{(pageMode === 'edit' || isLiveEditMode) && (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={checkUnsavedOnClickCommentsButton({ triggeredByKeyboardShortcut: true })}
				/>
			)}
		</>
	);

	if (collapsed) {
		return shortcutListeners;
	}

	return (
		<div
			css={commentIconContainerStyle}
			data-testid={dataTestId}
			data-vc="object-sidebar-comments-button"
		>
			<Tooltip
				position="left"
				content={
					isShortcutTextShown && !fg('platform_tooltip_shortcuts_migration_1') ? (
						<ShortcutVisualizer
							shortcut={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
							contentBefore={formatMessage(i18n.showCommentsLabel)}
							isEditorShortcut={pageMode === 'edit' || isLiveEditMode}
							isTooltip
						/>
					) : (
						<FormattedMessage {...i18n.showCommentsLabel} />
					)
				}
				shortcut={
					isShortcutTextShown && fg('platform_tooltip_shortcuts_migration_1')
						? commentShortcutStrings
						: undefined
				}
				delay={0}
				hideTooltipOnClick
			>
				{allowComments && (
					<>
						<IconButton
							onClick={checkUnsavedOnClickCommentsButton({ isViewAllIcon })}
							appearance="subtle"
							spacing={isStandaloneComment ? 'compact' : undefined} // Needed to get the correct overall size to match the X button
							shape={isRovoButtonEnabled ? 'default' : 'circle'}
							icon={
								isStandaloneComment &&
								expValEquals('confluence_comments_cohiti_panel_icon_update', 'isEnabled', true)
									? PanelRightIcon
									: CommentIcon
							}
							label={formatMessage(i18n.openCommentsPanelLabel)}
							testId="sidebar-comments-button"
							interactionName="view-comments-menu-action"
							{...(expVal('confluence_frontend_comments_a11y_kb_exp', 'isEnabled', false)
								? { ariaLabel: formatMessage(i18n.viewAllCommentsLabel) }
								: {})}
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
						/>
						{!isStandaloneComment && unreadCommentIDSet?.size > 0 && <UnreadIndicator />}
					</>
				)}
				{!isStandaloneComment && shortcutListeners}
			</Tooltip>
		</div>
	);
};
